import { cn } from '@/lib/utils';
import { OrderResponse } from '@/types/order-type';
import { Ref } from 'react';
import { DropSlot } from '../DropSlot';
import { OrderSlotRow } from '../OrderSlotRow';
import { DROP_SLOT_ROW_HEIGHT } from './pinned-board-constants';

type VirtualRange = {
  startIndex: number;
  endIndex: number;
  offsetY: number;
  totalHeight: number;
  visibleRows: number[];
};

export type PinnedOrderTableProps = {
  scrollContainerRef: Ref<HTMLDivElement>;
  isAssignDragActive: boolean;
  isDragOverTable: boolean;
  isCreatingOrder: boolean;
  orderForVariants: OrderResponse[];
  virtualRange: VirtualRange;
  targetDropSlotNo: number | null;
  dragPreviewName: string | null;
  selectedAssignActive?: boolean;
  nextDropSlotNo?: number;
  overflow: number;
  variantId: string;
  variantName: string;
  onScroll: (scrollTop: number) => void;
  onTapAssign?: (slotNo: number) => void;
  onOrderClick: (order: OrderResponse) => void;
  onOrderRefresh: () => void;
};

export function PinnedOrderTable({
  scrollContainerRef,
  isAssignDragActive,
  isDragOverTable,
  isCreatingOrder,
  orderForVariants,
  virtualRange,
  targetDropSlotNo,
  dragPreviewName,
  selectedAssignActive = false,
  nextDropSlotNo,
  overflow,
  variantId,
  variantName,
  onScroll,
  onTapAssign,
  onOrderClick,
  onOrderRefresh,
}: PinnedOrderTableProps) {
  return (
    <div
      className={cn(
        'flex h-[300px] min-h-[300px] shrink-0 flex-col overflow-hidden rounded-lg border sm:h-[340px] md:h-[380px] lg:h-auto lg:min-h-[360px] lg:max-h-[calc(100vh-14rem)] lg:flex-1',
        isAssignDragActive && !isDragOverTable && 'ring-2 ring-blue-300 ring-offset-1',
        isDragOverTable && 'ring-2 ring-blue-500 ring-offset-1',
        selectedAssignActive && !isDragOverTable && 'ring-2 ring-indigo-300 ring-offset-1',
      )}
    >
      <div className="grid shrink-0 grid-cols-[44px,1fr] border-b border-slate-200 bg-slate-100 sm:grid-cols-[52px,1fr] md:grid-cols-[60px,1fr]">
        <div className="px-2 py-1.5 text-[10px] font-medium text-slate-600 sm:px-3 sm:py-2 sm:text-[11px]">
          Slot
        </div>
      </div>

      <div
        ref={scrollContainerRef}
        onScroll={(event) => onScroll(event.currentTarget.scrollTop)}
        className={cn(
          'min-h-0 flex-1 overflow-y-auto bg-white transition-colors',
          isDragOverTable && 'bg-blue-50/40',
          selectedAssignActive && !isDragOverTable && 'bg-indigo-50/30',
          isCreatingOrder && 'pointer-events-none opacity-80',
        )}
      >
        {orderForVariants.map((order, inx) => (
          <OrderSlotRow
            key={order.id}
            customerName={order.customerName}
            totalProducts={order.totalItems || 0}
            totalAmount={order.totalAmount || 0}
            slotNo={inx + 1}
            status={order.status}
            orderNumber={order.orderNumber}
            orderId={order.id}
            variantId={variantId}
            onClick={() => onOrderClick(order)}
            refresh={onOrderRefresh}
            createdAt={order.createdAt || ''}
            productVariantName={variantName}
          />
        ))}

        <div
          style={{ height: virtualRange.totalHeight, position: 'relative' }}
          className="w-full shrink-0"
        >
          <div style={{ transform: `translateY(${virtualRange.offsetY}px)` }} className="w-full">
            {virtualRange.visibleRows.map((slotNo) => {
              const absoluteSlotNo = orderForVariants.length + slotNo;
              const isDropTarget =
                (isDragOverTable && targetDropSlotNo === absoluteSlotNo) ||
                (selectedAssignActive &&
                  (targetDropSlotNo === absoluteSlotNo ||
                    (nextDropSlotNo != null && absoluteSlotNo === nextDropSlotNo)));
              const isTapTarget = selectedAssignActive && onTapAssign;

              return (
                <div
                  key={slotNo}
                  style={{ height: DROP_SLOT_ROW_HEIGHT }}
                  className="shrink-0 overflow-hidden"
                >
                  <DropSlot
                    slotNo={absoluteSlotNo}
                    isAssigning={isCreatingOrder}
                    isDropTarget={isDropTarget}
                    dropPreviewName={isDropTarget ? dragPreviewName : null}
                    dimmed={
                      (isAssignDragActive && isDragOverTable && !isDropTarget) ||
                      (selectedAssignActive && !isDropTarget)
                    }
                    isTapTarget={!!isTapTarget}
                    onTapAssign={isTapTarget ? () => onTapAssign(absoluteSlotNo) : undefined}
                    subtitle={selectedAssignActive ? `Tap slot to assign` : 'Drop comment here'}
                  />
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {overflow > 0 && (
        <div className="border-t bg-slate-50 px-3 py-2 text-center text-[11px] text-slate-500">
          +{overflow} more slot(s)
        </div>
      )}
    </div>
  );
}
